/**
 * Finance > Invoices. Grid of vendor invoices captured from email.
 *
 * One row per invoice document. Click a row to review the PDF side-by-side with
 * the extracted vendor, dates, and totals. Mirrors the Compliance CoAs list.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Archive, Download, Loader2, Pencil, Upload } from 'lucide-react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ClearableSearchInput } from '@/components/ClearableSearchInput';
import { SortableHeader, gridTheadClassName, type SortDir } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { LockIndicator } from '@/components/LockIndicator';
import { OpenDocumentButton, DismissRecordButton } from '@/components/RowActionIcons';
import { InvoiceNotesPopover } from './InvoiceNotesPopover';
import { MatchedVendor, VendorSource } from './VendorPopup';
import { SyncToSageModal } from './SyncToSageModal';
import { SyncToBcModal } from './SyncToBcModal';
import { UploadInvoiceModal } from './UploadInvoiceModal';
import { useSession } from '@/lib/useSession';
import { formatDateShort, formatInstantDate } from '@/lib/dateUtils';
import {
  combinedInvoiceStatus,
  combinedStatusLabel,
  downloadInvoicesPdfZip,
  downloadInvoicesXlsx,
  filterByInvoiceDateRange,
  formatInvoiceTotal,
  hasDocumentsPush,
  hasDocumentsRead,
  hasDocumentsWrite,
  invoicePdfUrl,
  useInvoiceFilters,
  useInvoices,
  useMarkNotInvoice,
  vendorErpLabel,
  COMBINED_STATUS_FILTER_OPTIONS,
  COMBINED_STATUS_TONES,
  SOURCE_FILTER_OPTIONS,
  type CombinedInvoiceStatus,
  type InvoiceFilters,
  type InvoiceRow,
  type InvoiceSource,
} from '@/lib/useInvoices';

// Combined Status pill: one value per row, folding the processing status and
// the ERP push lifecycle together (COCKPIT.md 4.5).
function StatusPill({ status }: { status: CombinedInvoiceStatus }) {
  return <Pill tone={COMBINED_STATUS_TONES[status]}>{combinedStatusLabel(status)}</Pill>;
}

// A row is eligible for Sync to Sage when it is matched to a Sage vendor and its
// combined status is Not in ERP (extracted, not yet in an ERP). The old model
// keyed on a "Ready to Sync" sync_status, which the single combined-status model
// removed, so nothing was selectable; the derived Not-in-ERP value is now the
// selectable set. The server still builds the CSV from the Sage-matched subset
// and 422s a selection with no Sage-matched rows, so this just mirrors that.
function isSageSyncEligible(row: InvoiceRow): boolean {
  return row.matched_vendor_erp === 'sage' && combinedInvoiceStatus(row) === 'not_in_erp';
}

// A row is eligible for Sync to BC when it is matched to a BC vendor and its
// combined status is Not in ERP. The server's BC arm filters to the BC-matched
// subset of the selection and 422s a selection with no BC-matched rows.
function isBcSyncEligible(row: InvoiceRow): boolean {
  return row.matched_vendor_erp === 'bc' && combinedInvoiceStatus(row) === 'not_in_erp';
}

// Either ERP can sync a Not-in-ERP row matched to that ERP.
function isSyncEligible(row: InvoiceRow): boolean {
  return isSageSyncEligible(row) || isBcSyncEligible(row);
}

// GL display: the readable account number, plus its description when present
// (e.g. "1310-00: Inventory-Ingredients Purchases"). Returns null when there is
// no GL code at all. Not exported (keeping this a component-only module avoids
// the react-refresh/only-export-components lint warning).
function glDisplay(
  code: string | null | undefined,
  desc: string | null | undefined,
): string | null {
  if (!code) return null;
  return desc ? `${code}: ${desc}` : code;
}

// Cap a long cell value so the grid never scrolls sideways: show the first
// `max` characters with an ellipsis, and keep the full value in the cell's
// title attribute (shown on hover). GL code+description and the invoice number
// can both be long; this bounds their on-screen width.
function ellipsize(value: string, max = 16): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

// Sortable columns for the Invoices grid. Like CoAs, the backend does not
// accept a sort param, so the sort runs client-side over the rows already
// loaded. The infinite-scroll sentinel keeps loading pages in the backend's
// default order; the chosen sort reorders whatever rows are currently in hand.
type InvoiceSortColumn =
  | 'email_date'
  | 'vendor'
  | 'source'
  | 'gl_code'
  | 'invoice_number'
  | 'invoice_date'
  | 'due_date'
  | 'total_amount'
  | 'status';

function invoiceSortValue(row: InvoiceRow, col: InvoiceSortColumn): string | number {
  switch (col) {
    case 'email_date': return row.email_date ?? '';
    case 'vendor': return (row.matched_vendor_name ?? row.vendor ?? '').toLowerCase();
    case 'source': return vendorErpLabel(row.matched_vendor_erp).toLowerCase();
    case 'gl_code': return (row.gl_code ?? '').toLowerCase();
    case 'invoice_number': return (row.invoice_number ?? '').toLowerCase();
    case 'invoice_date': return row.invoice_date ?? '';
    case 'due_date': return row.due_date ?? '';
    case 'total_amount': return row.total_amount ?? -Infinity;
    case 'status': return combinedStatusLabel(combinedInvoiceStatus(row)).toLowerCase();
  }
}

function sortInvoiceRows(rows: InvoiceRow[], col: InvoiceSortColumn, dir: SortDir): InvoiceRow[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = invoiceSortValue(a, col);
    const bv = invoiceSortValue(b, col);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
}

// Whitelist the sortable column keys so a hand-edited URL can never select a
// column the grid does not render. The Invoices sort stays client-side (the api
// list endpoint takes no sort param), so this only decodes the URL state.
// Mirrors parseSortParam in commerce/Orders.tsx.
const SORTABLE_INVOICE_COLUMNS: ReadonlySet<InvoiceSortColumn> = new Set<InvoiceSortColumn>([
  'email_date',
  'vendor',
  'source',
  'gl_code',
  'invoice_number',
  'invoice_date',
  'due_date',
  'total_amount',
  'status',
]);

function parseInvoiceSortParam(
  raw: string | null,
): { column: InvoiceSortColumn; dir: SortDir } | null {
  if (!raw) return null;
  const [col, dir] = raw.split(':');
  if (!col || !dir) return null;
  if (!SORTABLE_INVOICE_COLUMNS.has(col as InvoiceSortColumn)) return null;
  if (dir !== 'asc' && dir !== 'desc') return null;
  return { column: col as InvoiceSortColumn, dir };
}

export function InvoicesList() {
  const { user } = useSession();
  const canView = hasDocumentsRead(user?.permissions, user?.superAdmin);
  const canWrite = hasDocumentsWrite(user?.permissions, user?.superAdmin);
  const canPush = hasDocumentsPush(user?.permissions, user?.superAdmin);
  const markNotInvoice = useMarkNotInvoice();

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const status = searchParams.get('status') ?? '';
  const vendor = searchParams.get('vendor') ?? '';
  const source = searchParams.get('source') ?? '';
  const invoiceDateFrom = searchParams.get('invoice_date_from') ?? '';
  const invoiceDateTo = searchParams.get('invoice_date_to') ?? '';

  const update = useCallback(
    (mutate: (sp: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );
  const setStr = (k: string, v: string) => update((sp) => (v ? sp.set(k, v) : sp.delete(k)));
  const resetFilters = () => update((sp) => ['q', 'status', 'vendor', 'source', 'sort', 'invoice_date_from', 'invoice_date_to'].forEach((k) => sp.delete(k)));

  // The Status filter is a combined value derived client-side (it folds the
  // processing status + ERP lifecycle), so it is not sent to the api list
  // endpoint. The api filters on q / vendor / source only.
  const filters: InvoiceFilters = useMemo(
    () => ({
      q: q || undefined,
      vendor: vendor || undefined,
      source: (source || undefined) as InvoiceSource | undefined,
    }),
    [q, vendor, source],
  );
  const statusFilter = (status || undefined) as CombinedInvoiceStatus | undefined;

  const invoicesQ = useInvoices(filters);
  const filtersQ = useInvoiceFilters();
  const [exporting, setExporting] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [syncOpen, setSyncOpen] = useState(false);
  const [bcSyncOpen, setBcSyncOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const onExport = async () => {
    setExporting(true);
    setToast(null);
    try {
      // Export honors the active Status filter too (the api now maps the
      // combined status to a WHERE clause), in addition to q / vendor / source.
      // Also forward the Invoice Date range so the download matches what the
      // grid shows instead of ignoring it (the grid applies this client-side
      // via filterByInvoiceDateRange, which the export request can't reuse).
      await downloadInvoicesXlsx({
        ...filters,
        status: statusFilter,
        invoice_date_from: invoiceDateFrom || undefined,
        invoice_date_to: invoiceDateTo || undefined,
      });
      setToast('Export downloaded.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  // Bundles invoices' PDFs into a single .zip, so Accounting can attach each one
  // when posting instead of downloading them one at a time. When rows are
  // checked, zips just that selection (document_ids); otherwise falls back to
  // the same filtered set as onExport. Mirrors onExport's error handling.
  const selectedIds = Array.from(selected);
  const MAX_ZIP_SELECTION = 40;
  const zipSelectionTooLarge = selectedIds.length > MAX_ZIP_SELECTION;
  const onExportZip = async () => {
    if (zipSelectionTooLarge) return;
    setExportingZip(true);
    setToast(null);
    try {
      await downloadInvoicesPdfZip(
        {
          ...filters,
          status: statusFilter,
          invoice_date_from: invoiceDateFrom || undefined,
          invoice_date_to: invoiceDateTo || undefined,
        },
        selectedIds,
      );
      setToast('PDF zip downloaded.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : String(err));
    } finally {
      setExportingZip(false);
    }
  };

  // Sort state lives in the URL (?sort=<column>:asc|desc) so opening an invoice
  // and clicking Back restores the chosen order, exactly as the filters already
  // do. The sort itself still runs client-side over the loaded rows (the api
  // list endpoint takes no sort param). Tri-state cycle per column:
  // asc -> desc -> off (param removed). Mirrors commerce/Orders.tsx.
  const sort = parseInvoiceSortParam(searchParams.get('sort'));
  const toggleSort = (col: InvoiceSortColumn) => {
    if (!sort || sort.column !== col) {
      update((sp) => sp.set('sort', `${col}:asc`));
    } else if (sort.dir === 'asc') {
      update((sp) => sp.set('sort', `${col}:desc`));
    } else {
      update((sp) => sp.delete('sort'));
    }
  };
  const sortedFor = (col: InvoiceSortColumn): SortDir | null =>
    sort && sort.column === col ? sort.dir : null;

  if (user && !canView) return <AccessDeniedCard />;

  // Apply the combined-status filter client-side (it is derived, not a server
  // field), then the invoice date range, then sort.
  const statusFilteredRows = statusFilter
    ? invoicesQ.rows.filter((r) => {
        const s = combinedInvoiceStatus(r);
        return statusFilter === 'in_erp' ? s.startsWith('in_erp') : s === statusFilter;
      })
    : invoicesQ.rows;
  const filteredRows = filterByInvoiceDateRange(
    statusFilteredRows,
    invoiceDateFrom || undefined,
    invoiceDateTo || undefined,
  );
  const rows = sort ? sortInvoiceRows(filteredRows, sort.column, sort.dir) : filteredRows;

  // Selection: any Not-in-ERP row matched to Sage or BC can be picked. The push
  // capability gates the whole selection UI. The two Sync buttons each act on
  // their own ERP's subset of the selection.
  const selectableIds = rows.filter(isSyncEligible).map((r) => r.document_id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };
  // Split the selection by ERP so each Sync button passes only its own rows.
  const selectedSageIds = rows
    .filter((r) => selected.has(r.document_id) && isSageSyncEligible(r))
    .map((r) => r.document_id);
  const selectedBcIds = rows
    .filter((r) => selected.has(r.document_id) && isBcSyncEligible(r))
    .map((r) => r.document_id);
  // Column count for the empty/loading/error spanning row. Nine data columns
  // (Date Received, Vendor, Source, GL Code, Invoice #, Invoice Date, Due Date,
  // Total, Status) plus the Actions column, plus the checkbox column when the
  // push capability is present.
  const colCount = (canPush ? 1 : 0) + 10;

  const filterBar = (
    <div className="flex flex-wrap gap-3 items-end">
      <div className="flex-1 min-w-[220px]">
        <label htmlFor="search" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Search</label>
        <ClearableSearchInput
          id="search"
          value={q}
          onChange={(v) => setStr('q', v)}
          placeholder="Vendor, invoice number, GL code, file name..."
          className="w-full border border-amp-line rounded pl-3 py-1.5 text-sm bg-amp-white text-amp-ink placeholder:text-amp-ink-muted dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink dark:placeholder:text-amp-dark-ink-muted"
        />
      </div>
      <div>
        <span className="inline-flex items-baseline text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">
          Vendor
        </span>
        <SearchableSelect
          ariaLabel="Vendor filter"
          options={filtersQ.options?.vendors ?? []}
          selected={vendor ? [vendor] : []}
          onChange={(v) => setStr('vendor', v[0] ?? '')}
          placeholder="Any"
        />
      </div>
      <div>
        <label htmlFor="status" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Status</label>
        <select
          id="status"
          value={status}
          onChange={(e) => setStr('status', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {COMBINED_STATUS_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div>
        <label htmlFor="source" className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mb-1">Source</label>
        <select
          id="source"
          value={source}
          onChange={(e) => setStr('source', e.target.value)}
          className="min-w-[140px] border border-amp-line rounded px-3 py-1.5 text-sm bg-amp-white text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        >
          <option value="">All</option>
          {SOURCE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">Invoice Date</span>
        <div className="flex items-center gap-1">
          <input
            id="invoice_date_from"
            type="date"
            value={invoiceDateFrom}
            max={invoiceDateTo || undefined}
            onChange={(e) => setStr('invoice_date_from', e.target.value)}
            aria-label="Invoice date from"
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted select-none">&#8211;</span>
          <input
            id="invoice_date_to"
            type="date"
            value={invoiceDateTo}
            min={invoiceDateFrom || undefined}
            onChange={(e) => setStr('invoice_date_to', e.target.value)}
            aria-label="Invoice date to"
            className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
          />
        </div>
      </div>
      {(q || status || vendor || source || invoiceDateFrom || invoiceDateTo) && (
        <button
          type="button"
          onClick={resetFilters}
          className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Reset filters
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Finance</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Invoices</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Vendor invoices captured from email. Click a row to review the PDF beside the extracted vendor, dates, and totals.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ToolPinToggle toolSlug="invoices" toolName="Invoices" />
          {canWrite && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
            >
              <Upload className="h-3 w-3" />
              Upload invoice
            </button>
          )}
          <button
            type="button"
            onClick={onExport}
            disabled={exporting}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            Export to Excel
          </button>
          <button
            type="button"
            onClick={onExportZip}
            disabled={exportingZip || zipSelectionTooLarge}
            title={zipSelectionTooLarge ? 'Select 40 or fewer to download as a zip' : undefined}
            className="inline-flex items-center gap-1 text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            {exportingZip ? <Loader2 className="h-3 w-3 animate-spin" /> : <Archive className="h-3 w-3" />}
            {selectedIds.length > 0 ? `Download ${selectedIds.length} PDFs (ZIP)` : 'Download PDFs (ZIP)'}
          </button>
        </div>
      </div>

      {toast && (
        <div className="mb-4 rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink" role="status">
          {toast}
        </div>
      )}

      {canPush && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-amp-line bg-amp-cream-warm px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
          <span className="text-sm text-amp-ink dark:text-amp-dark-ink">{selected.size} selected</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="text-xs border border-amp-line rounded px-3 py-1 text-amp-ink hover:bg-amp-white dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface"
            >
              Clear
            </button>
            {selectedSageIds.length > 0 && (
              <button
                type="button"
                onClick={() => setSyncOpen(true)}
                className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
              >
                <Upload className="h-3 w-3" /> Sync to Sage ({selectedSageIds.length})
              </button>
            )}
            {selectedBcIds.length > 0 && (
              <button
                type="button"
                onClick={() => setBcSyncOpen(true)}
                className="text-xs border border-amp-ink bg-amp-ink text-amp-white rounded px-3 py-1 hover:opacity-90 dark:border-amp-dark-ink dark:bg-amp-dark-ink dark:text-amp-dark-surface inline-flex items-center gap-1"
              >
                <Upload className="h-3 w-3" /> Sync to BC ({selectedBcIds.length})
              </button>
            )}
          </div>
        </div>
      )}

      <DataGridShell
        total={invoicesQ.totalCount}
        loaded={rows.length}
        countNoun="invoices"
        toolbar={filterBar}
        onReachEnd={invoicesQ.fetchNextPage}
        hasMore={invoicesQ.hasNextPage}
        isLoadingMore={invoicesQ.isFetchingNextPage}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              {canPush && (
                <th className="text-left font-medium px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all eligible"
                  />
                </th>
              )}
              <SortableHeader
                label="Date Received"
                sorted={sortedFor('email_date')}
                onToggle={() => toggleSort('email_date')}
                thClassName="px-3 py-2 w-[112px]"
              />
              <SortableHeader
                label="Vendor"
                sorted={sortedFor('vendor')}
                onToggle={() => toggleSort('vendor')}
                thClassName="px-3 py-2"
              />
              <SortableHeader
                label="Source"
                sorted={sortedFor('source')}
                onToggle={() => toggleSort('source')}
                thClassName="px-3 py-2 w-[80px]"
              />
              <SortableHeader
                label="GL Code"
                sorted={sortedFor('gl_code')}
                onToggle={() => toggleSort('gl_code')}
                thClassName="px-3 py-2 w-[100px]"
              />
              <SortableHeader
                label="Invoice #"
                sorted={sortedFor('invoice_number')}
                onToggle={() => toggleSort('invoice_number')}
                thClassName="px-3 py-2 w-[120px]"
              />
              <SortableHeader
                label="Invoice Date"
                sorted={sortedFor('invoice_date')}
                onToggle={() => toggleSort('invoice_date')}
                thClassName="px-3 py-2 w-[92px]"
              />
              <SortableHeader
                label="Due Date"
                sorted={sortedFor('due_date')}
                onToggle={() => toggleSort('due_date')}
                thClassName="px-3 py-2 w-[88px]"
              />
              <SortableHeader
                label="Total"
                sorted={sortedFor('total_amount')}
                onToggle={() => toggleSort('total_amount')}
                align="right"
                thClassName="px-3 py-2 w-[104px]"
              />
              <SortableHeader
                label="Status"
                sorted={sortedFor('status')}
                onToggle={() => toggleSort('status')}
                thClassName="px-3 py-2 w-[140px]"
              />
              <th className="px-3 py-2 text-left font-medium w-[96px]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {invoicesQ.isLoading ? (
              <tr><td colSpan={colCount} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading invoices...</td></tr>
            ) : invoicesQ.error ? (
              <tr><td colSpan={colCount} className="px-4 py-6 text-red-700 dark:text-red-300">Could not load: {invoicesQ.error.message}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">No invoices match this filter.</td></tr>
            ) : (
              rows.map((row) => (
                <InvoiceListRow
                  key={row.document_id}
                  row={row}
                  canWrite={canWrite}
                  canPush={canPush}
                  selectable={isSyncEligible(row)}
                  selected={selected.has(row.document_id)}
                  onToggleSelect={() => toggleSelect(row.document_id)}
                  onDismiss={(documentId) => markNotInvoice.mutate({ documentId })}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {syncOpen && (
        <SyncToSageModal
          invoiceIds={selectedSageIds}
          onClose={() => setSyncOpen(false)}
          onSynced={() => setSelected(new Set())}
        />
      )}

      {bcSyncOpen && (
        <SyncToBcModal
          invoiceIds={selectedBcIds}
          onClose={() => setBcSyncOpen(false)}
          onSynced={() => setSelected(new Set())}
        />
      )}

      {uploadOpen && (
        <UploadInvoiceModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => invoicesQ.refetch()}
        />
      )}
    </div>
  );
}

function InvoiceListRow({
  row,
  canWrite,
  canPush,
  selectable,
  selected,
  onToggleSelect,
  onDismiss,
}: {
  row: InvoiceRow;
  canWrite: boolean;
  canPush: boolean;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onDismiss: (documentId: string) => void;
}) {
  const detailHref = {
    pathname: `/finance/invoices/${row.document_id}`,
    search: typeof window !== 'undefined' ? window.location.search : '',
  };
  const iconBtnCls =
    'text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink';
  return (
    <tr className="hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm">
      {canPush && (
        <td className="px-3 py-2">
          <input
            type="checkbox"
            checked={selected}
            disabled={!selectable}
            onChange={onToggleSelect}
            aria-label={selectable ? 'Select invoice' : 'Not eligible for sync'}
          />
        </td>
      )}
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">
        {formatInstantDate(row.email_date)}
      </td>
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink max-w-[200px]">
        <MatchedVendor fields={row} extractedVendor={row.vendor} />
      </td>
      <td className="px-3 py-2">
        <VendorSource erp={row.matched_vendor_erp} />
      </td>
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink whitespace-nowrap" title={glDisplay(row.gl_code, row.gl_account_desc) ?? undefined}>
        {(() => {
          const gl = glDisplay(row.gl_code, row.gl_account_desc);
          return gl ? ellipsize(gl) : '--';
        })()}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink max-w-[120px]" title={row.invoice_number ?? undefined}>
        <span className="block truncate">{row.invoice_number || '--'}</span>
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(row.invoice_date)}</td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted whitespace-nowrap">{formatDateShort(row.due_date)}</td>
      <td className="px-3 py-2 text-right text-amp-ink dark:text-amp-dark-ink whitespace-nowrap">
        {formatInvoiceTotal(row.total_amount, row.currency)}
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <StatusPill status={combinedInvoiceStatus(row)} />
          <LockIndicator locked={row.locked} />
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2">
          <Link to={detailHref} title="Edit" aria-label="Edit" className={iconBtnCls}>
            <Pencil className="h-4 w-4" aria-hidden />
          </Link>
          <InvoiceNotesPopover
            documentId={row.document_id}
            notesCount={row.notes_count ?? 0}
            canWrite={canWrite}
          />
          <OpenDocumentButton url={invoicePdfUrl(row.document_id)} label="Open PDF" />
          {canWrite && (
            <DismissRecordButton onConfirm={() => onDismiss(row.document_id)} />
          )}
        </div>
      </td>
    </tr>
  );
}

function AccessDeniedCard() {
  return (
    <div className="max-w-xl rounded-lg border border-amp-line p-5 dark:border-amp-dark-line">
      <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-1">No access</h2>
      <p className="text-sm text-amp-ink dark:text-amp-dark-ink">
        Invoices is gated by the documents.read capability. Ask IT to grant you access.
      </p>
    </div>
  );
}
